import { Navigate, useLocation } from 'react-router-dom'
import { useMemo } from 'react'

import {
  getServiceInstanceIdFromPost,
  resolveActiveServiceInstanceId,
  resolveWorkspaceActiveServiceInstanceId,
  resolveWorkspacePostContext,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { isOrganizationServiceVariant } from '@/lib/service-variant-types'
import { useNodeLinkedServiceInstances } from '@/hooks/use-node-linked-service-instances'
import { useAuthStore } from '@/stores/auth-store'

type VariantRouteRule = {
  uniqueIds: string[]
  labels: string[]
  path: string
}

const VARIANT_HOME_ROUTES: VariantRouteRule[] = [
  { uniqueIds: ['700'], labels: ['retail'], path: '/services/pos' },
  { uniqueIds: ['1600'], labels: ['fundraising', 'charity'], path: '/fundraising' },
  { uniqueIds: ['2000'], labels: ['temple'], path: '/venue/dashboard' },
]

function normalizeToken(value?: string | number | null) {
  return String(value ?? '').trim().toLowerCase()
}

function resolveServiceVariantHomePath(
  variantUniqueId?: string | number | null,
  variantLabel?: string | null,
) {
  const uniqueId = normalizeToken(variantUniqueId)
  const label = normalizeToken(variantLabel)

  const rule = VARIANT_HOME_ROUTES.find((item) => (
    item.uniqueIds.includes(uniqueId) || item.labels.includes(label)
  ))

  return rule?.path || '/dashboard'
}

export default function ServiceVariantDashboard() {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const params = new URLSearchParams(location.search)
  const baseServiceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post: currentPost,
    storedServiceInstanceId,
  }) || getServiceInstanceIdFromPost(currentPost)
  const workspaceNodeId = String(currentPost?.node_id ?? '').trim()
  const nodeLinkedServiceInstances = useNodeLinkedServiceInstances({
    nodeId: workspaceNodeId,
    activeServiceInstanceId: baseServiceInstanceId,
    enabled: Boolean(workspaceNodeId),
  })
  const serviceInstanceId = useMemo(
    () => resolveWorkspaceActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      storedServiceInstanceId,
      nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
    }) || baseServiceInstanceId,
    [
      baseServiceInstanceId,
      currentPost,
      location.search,
      nodeLinkedServiceInstances.serviceOptions,
      storedServiceInstanceId,
    ],
  )
  const workspacePost = useMemo(
    () => resolveWorkspacePostContext({
      post: currentPost,
      availablePosts,
      serviceInstanceId,
      nodeInstances: nodeLinkedServiceInstances.instances,
    }) ?? currentPost,
    [availablePosts, currentPost, nodeLinkedServiceInstances.instances, serviceInstanceId],
  )
  const variantUniqueId = workspacePost?.service_variant_unique_id ?? params.get('variant')
  const variantLabel = workspacePost?.service_variant_name ?? workspacePost?.service_variant_type ?? params.get('variant')

  if (isOrganizationServiceVariant(variantUniqueId, variantLabel)) {
    return <Navigate to={withServiceInstanceId('/dashboard', serviceInstanceId)} replace />
  }

  return <Navigate to={withServiceInstanceId(resolveServiceVariantHomePath(variantUniqueId, variantLabel), serviceInstanceId)} replace />
}
